import classNames from "../../../bpmn.io/classnames";
import {
  Errors,
  FormContext,
  Description,
  Label,
  Textfield,
} from "../../../bpmn.io/@bpmn-io/form-js";

function _bspEnsureSpinnerStyle() {
  if (!document.getElementById('_bsp-spin-style')) {
    const s = document.createElement('style');
    s.id = '_bsp-spin-style';
    s.textContent = '@keyframes _bspSpin{to{transform:rotate(360deg)}}';
    document.head.appendChild(s);
  }
}
function _bspCreateSpinner() {
  const wrap = document.createElement('div');
  wrap.style.cssText = 'display:flex;align-items:center;justify-content:center;height:120px;width:100%;';
  const ring = document.createElement('div');
  ring.style.cssText = 'width:48px;height:48px;border:5px solid #e0e0e0;border-top-color:#0078d4;border-radius:50%;animation:_bspSpin 0.8s linear infinite;';
  wrap.appendChild(ring);
  return wrap;
}

import { useContext, useState } from "preact/hooks";
import { html } from "htm/preact";

export const fileInputType = "viewerButton";
import { AppData } from "../../helpers/app-data";

import { icon } from "./iconBase64";

/**
 * Resuelve un valor en un objeto usando notación de punto.
 * Ej: getByPath({ flujo: { archivo: 10 } }, 'flujo.archivo') → 10
 */
function getByPath(obj, dotPath) {
  if (!obj || !dotPath) return null;
  const parts = dotPath.split('.');
  let current = obj;
  for (const part of parts) {
    if (current === null || current === undefined || typeof current !== 'object') {
      return null;
    }
    current = current[part];
  }
  return current !== undefined ? current : null;
}

export function FileInputViewRenderer(props) {
  const { disabled, errors = [], field } = props;

  const { description, id, label, validate = {}, llave } = field;
  const { required } = validate;
  const contextValue = useContext(FormContext);
  const { formId } = contextValue;
  const errorMessageId =
    errors.length === 0 ? undefined : `${prefixId(id, formId)}-error-message`;

  const [isLoading, setIsLoading] = useState(false);

  const contentId = `documentContent-${id}`;

  /** Obtiene el ID del archivo resolviendo la ruta `llave` en los datos actuales del formulario. */
  function resolveFileId() {
    if (!llave) return null;

    // 1. Intentar desde los datos del formulario (campos vinculados)
    try {
      const form = contextValue.getService('form');
      if (form) {
        const data = form._getState().data;
        const val = getByPath(data, llave);
        if (val !== null && val !== undefined && val !== '') return val;
      }
    } catch (e) {
      console.warn('No se pudo leer desde form data:', e);
    }

    // 2. Fallback: buscar en window.aditionalFlowData
    // La llave puede venir como "flujo.firma" pero en aditionalFlowData la clave es "firma"
    try {
      const aditional = window.aditionalFlowData;
      if (aditional) {
        const valFull = getByPath(aditional, llave);
        if (valFull !== null && valFull !== undefined && valFull !== '') return valFull;

        const flujoPrefix = 'flujo.';
        const shortKey = llave.startsWith(flujoPrefix) ? llave.slice(flujoPrefix.length) : llave;
        const valShort = aditional[shortKey];
        if (valShort !== null && valShort !== undefined && valShort !== '') return valShort;
      }
    } catch (e) {
      console.warn('No se pudo leer desde aditionalFlowData:', e);
    }

    // 3. Fallback: buscar en window.processVariables (mapa id→valor cargado al iniciar el formulario)
    // Resuelve el caso donde la variable existe en el proceso pero no tiene campo en el formulario
    try {
      const procVars = window.processVariables;
      if (procVars) {
        // Intentar ruta completa (ej: "flujo.firma" → procVars["flujo.firma"])
        if (procVars[llave] !== undefined && procVars[llave] !== null && procVars[llave] !== '') {
          return procVars[llave];
        }
        // Quitar prefijo "flujo." (ej: "flujo.firma" → procVars["firma"])
        const flujoPrefix = 'flujo.';
        const shortKey = llave.startsWith(flujoPrefix) ? llave.slice(flujoPrefix.length) : llave;
        if (procVars[shortKey] !== undefined && procVars[shortKey] !== null && procVars[shortKey] !== '') {
          return procVars[shortKey];
        }
      }
    } catch (e) {
      console.warn('No se pudo leer desde processVariables:', e);
    }

    return null;
  }

  function buildHeaders() {
    const API_TOKEN = sessionStorage.getItem(AppData.localStorageToken);
    return {
      "Content-Type": "application/json",
      "X-APIKey": AppData.API_KEY,
      Authorization: `Bearer ${API_TOKEN}`,
    };
  }

  async function fetchDocumentData(fileId) {
    const url = `${AppData.API_URL}EjecucionProceso/ConsultarDocumentoBSP?IdDocumentoEjecucion=${fileId}`;
    const response = await fetch(url, { method: "GET", headers: buildHeaders() });
    if (!response.ok) throw new Error("Network response was not ok");
    const json = await response.json();
    // Estructura: { Ok, Data: "<url string>" }
    return json.Data || null;
  }

  const handleView = async () => {
    const fileId = resolveFileId();
    if (fileId === null || fileId === undefined || fileId === '') {
      alert("No se encontró el ID del archivo. Verifique la propiedad 'Llave' del componente.");
      return;
    }

    setIsLoading(true);
    const contentEl = document.getElementById(contentId);
    try {
      const blobUrl = await fetchDocumentData(fileId);
      if (!blobUrl) {
        alert("El documento no está disponible para visualización.");
        if (contentEl) contentEl.style.display = "none";
        return;
      }

      const _extUrl1 = blobUrl.toLowerCase().replace(/\?.*$/, '');
      // Archivos comprimidos: sin visor disponible en el navegador → descargar directamente
      if (/\.(zip|rar|7z)$/.test(_extUrl1)) {
        const _dlA1 = document.createElement('a');
        _dlA1.href = blobUrl;
        _dlA1.download = blobUrl.split('/').pop()?.split('?')[0] || 'archivo';
        _dlA1.target = '_blank';
        _dlA1.style.display = 'none';
        document.body.appendChild(_dlA1);
        _dlA1.click();
        document.body.removeChild(_dlA1);
        if (contentEl) {
          contentEl.innerHTML = '<p style="padding:1em;color:#555;">Archivo comprimido — la descarga ha iniciado.</p>';
          contentEl.style.display = 'block';
        }
        return;
      }
      let _viewerEl1;
      if (/\.(png|jpe?g|gif|webp|svg|bmp|ico)$/.test(_extUrl1)) {
        _viewerEl1 = document.createElement('img');
        _viewerEl1.style = 'max-width: 100%; max-height: 30em; display: block; margin: auto; padding: 1em;';
        _viewerEl1.src = blobUrl;
      } else {
        _viewerEl1 = document.createElement('iframe');
        _viewerEl1.style = 'width: 100%; height: 30em; padding: 2em; border: none;';
        if (/\.(xlsx?|docx?|pptx?|csv)$/.test(_extUrl1)) {
          _viewerEl1.src = 'https://view.officeapps.live.com/op/embed.aspx?src=' + encodeURIComponent(blobUrl);
        } else if (/\.(pdf|txt|html?|xml)$/.test(_extUrl1)) {
          _viewerEl1.src = blobUrl;
        } else {
          _viewerEl1.src = 'https://docs.google.com/gview?url=' + encodeURIComponent(blobUrl) + '&embedded=true';
        }
      }
      if (contentEl) {
        contentEl.innerHTML = "";
        _bspEnsureSpinnerStyle();
        const _sp1 = _bspCreateSpinner();
        contentEl.appendChild(_sp1);
        contentEl.style.display = "block";
        _viewerEl1.onload = () => _sp1.remove();
        contentEl.appendChild(_viewerEl1);
      }
    } catch (error) {
      alert("No se encontró el documento.");
      console.error("Error al visualizar el documento:", error);
      if (contentEl) contentEl.style.display = "none";
    } finally {
      setIsLoading(false);
    }
  };

  const handleDownload = async () => {
    const fileId = resolveFileId();
    if (fileId === null || fileId === undefined || fileId === '') {
      alert("No se encontró el ID del archivo. Verifique la propiedad 'Llave' del componente.");
      return;
    }

    setIsLoading(true);
    try {
      const blobUrl = await fetchDocumentData(fileId);
      if (!blobUrl) {
        alert("El documento no está disponible para descarga.");
        return;
      }

      const fileResponse = await fetch(blobUrl);
      const blob = await fileResponse.blob();
      const url = window.URL.createObjectURL(blob);
      // Extraer nombre del archivo desde la URL si es posible
      const fileName = blobUrl.split('/').pop()?.split('?')[0] || 'archivo';
      const a = document.createElement("a");
      a.href = url;
      a.download = fileName;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      alert("No se pudo descargar el documento.");
      console.error("Error al descargar el documento:", error);
    } finally {
      setIsLoading(false);
    }
  };

  return html`
    <div class=${formFieldClasses(fileInputType, { errors, disabled })}>
      <${Label}
        id=${prefixId(id, formId)}
        label=${label}
        required=${required}
      />
      <div style="display:flex;gap:8px;flex-wrap:wrap;">
        <button
          class="fjs-button"
          disabled=${disabled || isLoading}
          type="button"
          onClick=${handleView}
        >
          <i class="ms-Icon ms-Icon--View" style="margin-right:4px;"></i>
          Ver archivo
        </button>
        <button
          class="fjs-button"
          disabled=${disabled || isLoading}
          type="button"
          onClick=${handleDownload}
        >
          <i class="ms-Icon ms-Icon--Download" style="margin-right:4px;"></i>
          Descargar
        </button>
      </div>
      <${Description} description=${description} />
      <${Errors} errors=${errors} id=${errorMessageId} />
    </div>
    <div id=${contentId} style="display:none;"></div>
  `;
}

// Configuración del renderer
FileInputViewRenderer.config = {
  ...Textfield.config,
  type: fileInputType,
  label: "Ver archivo",
  propertiesPanelEntries: [],
  iconUrl: icon,

};

// Función helper para definir clases de los campos
function formFieldClasses(
  type,
  { errors = [], disabled = false, readonly = false } = {}
) {
  if (!type) {
    throw new Error("type required");
  }

  return classNames("fjs-form-field", `fjs-form-field-${type}`, {
    "fjs-has-errors": errors.length > 0,
    "fjs-disabled": disabled,
    "fjs-readonly": readonly,
  });
}

// Función helper para generar ID con prefijo
function prefixId(id, formId) {
  if (formId) {
    return `fjs-form-${formId}-${id}`;
  }
  return `fjs-form-${id}`;
}
